<template>
    <nio-modal
        name="contact"
        size="md"
        :title="modalTitle"
        ref="modal"
        @closed="reset"
    >
        <div class="space-y-8">
            <section class="space-y-6">
                <div class="space-y-6 | md:flex md:space-x-6 md:space-y-0">
                    <nio-input-group class="flex-1" :label="$t('contacts.contactModal.firstName')">
                        <nio-input v-model="contact.firstName" />
                    </nio-input-group>
                    <nio-input-group class="flex-1" :label="$t('contacts.contactModal.lastName')">
                        <nio-input v-model="contact.lastName" />
                    </nio-input-group>
                </div>
                <div class="space-y-6 | md:flex md:space-x-6 md:space-y-0">
                    <nio-input-group class="flex-1" :label="$t('contacts.contactModal.civility')">
                        <nio-select :options="civilities" v-model="contact.civility" />
                    </nio-input-group>
                    <nio-input-group class="flex-1" :label="$t('contacts.contactModal.locale')">
                        <nio-select :options="locales" v-model="contact.locale" />
                    </nio-input-group>
                </div>
                <div class="space-y-6 | md:flex md:space-x-6 md:space-y-0">
                    <nio-input-group class="flex-1" :label="$t('contacts.contactModal.email')">
                        <nio-input v-model="contact.email" />
                    </nio-input-group>
                    <nio-input-group class="flex-1" :label="$t('contacts.contactModal.communicationPreference')">
                        <nio-select :options="communicationPreferences" v-model="contact.communicationPreference" />
                    </nio-input-group>
                </div>
                <div class="space-y-6 | md:flex md:space-x-6 md:space-y-0" v-if="isEdit">
                    <nio-input-group class="flex-1" :label="$t('contacts.contactModal.subscribedSms')">
                        <template #after-label v-if="!subscribedToSmsPickerEnabled">
                            <nio-icon class="ml-1" name="bold/question-help-circle" v-tooltip="$t('contacts.contactModal.onlyUserMayResubscribe')" />
                        </template>
                        <nio-toggle :disabled="!subscribedToSmsPickerEnabled" v-model="contact.subscribed">
                            <span v-text="subscribedText" />
                        </nio-toggle>
                    </nio-input-group>
                    <nio-input-group class="flex-1" :label="$t('contacts.contactModal.subscribedEmail')">
                        <template #after-label v-if="!subscribedToEmailPickerEnabled">
                            <nio-icon class="ml-1" name="bold/question-help-circle" v-tooltip="$t('contacts.contactModal.onlyUserMayResubscribe')" />
                        </template>
                        <nio-toggle :disabled="!subscribedToEmailPickerEnabled" v-model="contact.subscribed_email">
                            <span v-text="subscribedEmailText" />
                        </nio-toggle>
                    </nio-input-group>
                </div>
                <div class="space-y-6 | md:flex md:space-x-6 md:space-y-0">
                    <nio-input-group class="flex-1" :label="$t('contacts.contactModal.notes')">
                        <nio-textarea
                            rows="3"
                            v-model="contact.notes"
                        />
                    </nio-input-group>
                </div>
            </section>

            <section>
                <h2 class="flex items-center text-xl mb-4">
                    <nio-icon class="mr-2" name="regular/phone" />
                    {{ $t('contacts.contactModal.phoneNumbers') }}
                </h2>
                <div class="space-y-6">
                    <contact-phone
                        :number.sync="phone.number"
                        :phone="phone"
                        :show-delete="showDeleteButton(phone)"
                        :updating="isEdit"
                        :key="index"
                        @delete-phone="deletePhone"
                        @update-phone="updatePhone"
                        v-for="(phone, index) in contact.phones"
                    />
                    <nio-button class="w-full" icon="regular/add" @click="addPhone">
                        {{ $t('contacts.contactModal.addPhone') }}
                    </nio-button>
                </div>
            </section>

            <error-message />
        </div>

        <template #footer>
            <nio-button class="flex-1" :disabled="saving" @click="close">
                {{ $t('global.general.cancel') }}
            </nio-button>
            <nio-button
                class="flex-1"
                color="blue"
                :loading="saving"
                @click="onSave"
            >
                {{ $t('global.general.save') }}
            </nio-button>
        </template>
    </nio-modal>
</template>

<script>
    import { mapActions, mapState, mapMutations } from 'vuex';
    import cloneDeep from 'lodash/cloneDeep.js';
    import ContactPhone from '@/components/ContactPhone.vue';

    export default {
        components: {
            ContactPhone,
        },

        data() {
            return {
                saving: false,
                subscribedToSmsPickerEnabled: true,
                subscribedToEmailPickerEnabled: true,
                contact: {
                    civility: '',
                    firstName: '',
                    lastName: '',
                    email: '',
                    locale: 'fr',
                    subscribed: true,
                    subscribed_email: true,
                    notes: '',
                    communicationPreference: '',
                    phones: [{ number: '' }],
                },
                deletedPhones: [],
                locales: [
                    { value: 'fr', label: 'Français' },
                    { value: 'en', label: 'English' },
                ],
            };
        },

        computed: {
            ...mapState({
                authUser: state => state.authUser,
                editingContact: state => state.contact.editingContact,
            }),

            subscribedEmailText() {
                return this.contact.subscribed_email
                    ? this.$t('contacts.contactModal.maySendEmail')
                    : this.$t('contacts.contactModal.mayNotSendEmail');
            },

            subscribedText() {
                return this.contact.subscribed
                    ? this.$t('contacts.contactModal.maySendSms')
                    : this.$t('contacts.contactModal.mayNotSendSms');
            },

            isEdit() {
                return !empty(this.editingContact);
            },

            modalTitle() {
                return this.isEdit ? this.$t('contacts.contactModal.editTitle') : this.$t('contacts.contactModal.addTitle');
            },

            communicationPreferences() {
                return [
                    { value: 'sms', label: this.$t('contacts.contactModal.sms') },
                    { value: 'email', label: this.$t('contacts.contactModal.email') },
                ];
            },

            civilities() {
                return [
                    { value: 'mr', label: this.$t('global.civility.mr') },
                    { value: 'ms', label: this.$t('global.civility.ms') },
                    { value: 'miss', label: this.$t('global.civility.miss') },
                    { value: 'dr', label: this.$t('global.civility.dr') },
                    { value: 'esq', label: this.$t('global.civility.esq') },
                ];
            },
        },

        watch: {
            editingContact() {
                if (empty(this.editingContact)) {
                    return;
                }

                this.contact.civility = this.editingContact.civility;
                this.contact.firstName = this.editingContact.first_name;
                this.contact.lastName = this.editingContact.last_name;
                this.contact.email = this.editingContact.email || '';
                this.contact.locale = this.editingContact.locale;
                this.contact.subscribed = this.editingContact.subscribed;
                this.contact.subscribed_email = this.editingContact.subscribed_email;
                this.contact.notes = this.editingContact.notes;
                this.contact.communicationPreference = this.editingContact.communication_preference;
                this.contact.phones = this.editingContact.phones.length ? cloneDeep(this.editingContact.phones) : [{ number: '' }];

                this.subscribedToSmsPickerEnabled = this.contact.subscribed;
                this.subscribedToEmailPickerEnabled = this.contact.subscribed_email;
            },
        },

        methods: {
            ...mapMutations('chat', [
                'updateConversationContactPhone',
            ]),

            ...mapMutations('contact', [
                'addContact',
                'setEditingContact',
            ]),

            ...mapActions('contact', [
                'createContact',
                'createContactPhone',
                'deleteContactPhone',
                'getContact',
                'updateContact',
                'updateContactPhone',
            ]),

            onSave() {
                if (this.isEdit) {
                    this.triggerUpdate();
                } else {
                    this.triggerCreate();
                }
            },

            async triggerCreate() {
                this.saving = true;

                let contactId;

                try {
                    const contact = await this.createContact({
                        civility: this.contact.civility,
                        first_name: this.contact.firstName,
                        last_name: this.contact.lastName,
                        email: this.contact.email,
                        locale: this.contact.locale,
                        notes: this.contact.notes,
                        communication_preference: this.contact.communicationPreference,
                        account_id: this.authUser.account.id,
                    });

                    contactId = contact.id;
                } catch ({ response }) {
                    this.saving = false;

                    this.$notify.show({
                        type: 'error',
                        message: this.$t('contacts.contactModal.notifications.errors.createContact'),
                    });

                    return;
                }

                try {
                    // Create phones
                    const phonePromises = [];

                    this.contact.phones
                        .filter(phone => {
                            return !!phone.number;
                        })
                        .forEach(phone => {
                            phonePromises.push(this.createContactPhone({
                                contact_id: contactId,
                                number: phone.number,
                            }));
                        });

                    await Promise.all(phonePromises);
                } catch ({ response }) {
                    this.saving = false;

                    this.$notify.show({
                        type: 'error',
                        message: this.$t('contacts.contactModal.notifications.errors.createPhone'),
                    });

                    return;
                }

                try {
                    const contact = await this.getContact(contactId);
                    this.addContact(contact);

                    this.close();

                    this.$notify.show({
                        type: 'success',
                        message: this.$t('contacts.contactModal.notifications.success.create'),
                    });
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$t('contacts.contactModal.notifications.errors.fetchContact'),
                    });
                } finally {
                    this.saving = false;
                }
            },

            async triggerUpdate() {
                this.saving = true;

                const phonePromises = [];

                // Create or update phones
                this.contact.phones
                    .filter(phone => {
                        return !!phone.number || !!phone.id;
                    })
                    .forEach(phone => {
                        if (phone.id && !phone.number) {
                            phonePromises.push(this.deleteContactPhone(phone.id));
                        } else if (phone.id) {
                            phonePromises.push(this.updateContactPhone({
                                id: phone.id,
                                payload: { number: phone.number },
                            }));
                        } else {
                            phonePromises.push(this.createContactPhone({
                                contact_id: this.editingContact.id,
                                number: phone.number,
                            }));
                        }
                    });

                // Delete phones
                this.deletedPhones.forEach(phone => {
                    phonePromises.push(this.deleteContactPhone(phone.id));
                });

                try {
                    await Promise.all(phonePromises);
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$t('contacts.contactModal.notifications.errors.editPhone'),
                    });

                    this.saving = false;

                    return;
                }

                try {
                    await this.updateContact({
                        id: this.editingContact.id,
                        payload: {
                            civility: this.contact.civility,
                            first_name: this.contact.firstName,
                            last_name: this.contact.lastName,
                            email: this.contact.email,
                            locale: this.contact.locale,
                            subscribed: this.contact.subscribed,
                            subscribed_email: this.contact.subscribed_email,
                            notes: this.contact.notes,
                            communication_preference: this.contact.communicationPreference,
                        },
                    });

                    this.close();

                    this.$notify.show({
                        type: 'success',
                        message: this.$t('contacts.contactModal.notifications.success.update'),
                    });
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$t('contacts.contactModal.notifications.errors.updateContact'),
                    });
                } finally {
                    this.saving = false;
                }
            },

            addPhone() {
                this.contact.phones.push({
                    number: '',
                });
            },

            updatePhone(updatedPhone) {
                this.contact.phones.filter(phone => phone.id == updatedPhone.id)
                    .forEach(phone => {
                        Object.assign(phone, updatedPhone);
                    });
            },

            deletePhone(phone) {
                let phoneIndex = null;

                if (phone.id) {
                    phoneIndex = this.contact.phones.findIndex(p => p.id == phone.id);

                    this.deletedPhones.push(this.contact.phones[phoneIndex]);
                } else {
                    phoneIndex = this.contact.phones.findIndex(p => p.number == phone.number && empty(p.id));
                }

                this.contact.phones.splice(phoneIndex, 1);
            },

            showDeleteButton(phone) {
                if (this.contact.phones.length < 2) {
                    return false;
                }

                if (empty(this.editingContact) || empty(this.editingContact.conversations)) {
                    return true;
                }

                const conversation = this.editingContact.conversations.find(c => c.contact_phone_id == phone.id);

                return empty(conversation);
            },

            reset() {
                this.contact.civility = '';
                this.contact.firstName = '';
                this.contact.lastName = '';
                this.contact.email = '';
                this.contact.locale = 'fr';
                this.contact.subscribed = true;
                this.contact.subscribed_email = true;
                this.contact.notes = '';
                this.contact.communicationPreference = '';
                this.contact.phones = [{ number: '' }];
                this.deletedPhones = [];

                this.subscribedToSmsPickerEnabled = true;
                this.subscribedToEmailPickerEnabled = true;

                this.setEditingContact({});
            },

            close() {
                this.$refs.modal.close();
            },
        },
    };
</script>
